import type { AxiosResponse } from "axios";

import { api, unauthenticatedApi } from "@/api/api";
import type {
  DeviceType,
  PlatformApi,
  SetNavigationBarParams,
} from "@/app/platform/types";
import { getDeviceTypeFromUserAgent } from "@/app/platform/utils";
import { CreditCardsConsentError } from "@/errors/CreditCardsConsentError";
import { PersonalDataConsentError } from "@/errors/PersonalDataConsentError";
import { SignInError } from "@/errors/SignInError";
import type {
  SignUpOutputDto,
  UpdateDetailsBodyInputDto,
} from "@/types/codegen";
import { isProduction } from "@/utils/constants";
import { promiseWithTimeout } from "@/utils/promiseWithTimeout";

import { bnplPaymentProvider } from "../payments/bnplPaymentProvider";
import { superQiPaymentProvider } from "../payments/superQiPaymentProvider";
import {
  type PaymentProviderType,
  type BnplPaymentProvider,
  type StandardPaymentProvider,
  type PaymentProvider,
} from "../payments/types";

const defaultSuperQiTimeoutMs = 5000;

class SuperQiApi implements PlatformApi {
  deviceType: DeviceType = getDeviceTypeFromUserAgent();

  init() {
    my.getSystemInfo({
      success: ({ platform }) => {
        if (platform === "iOS" || platform === "Android") {
          this.deviceType = platform;
        } else {
          this.deviceType = "Other";
        }
      },
    });
  }

  getLocale() {
    return promiseWithTimeout<string>(
      (resolve, reject) => {
        my.getSystemInfo({
          success: (res) => {
            const fetchedLocale = res.language.split("-")[0];
            resolve(fetchedLocale);
          },
          fail: (error) => {
            reject(new Error("Failed to get system info", { cause: error }));
          },
        });
      },
      {
        timeoutMs: defaultSuperQiTimeoutMs,
        timeoutError: new Error("Failed to get system info", {
          cause: "timeout",
        }),
      },
    );
  }

  setNavigationBar({ backgroundColor, image }: SetNavigationBarParams) {
    my?.setNavigationBar({
      backgroundColor,
      // this is a workaround to hide the logo, as there is no api explicitely doing that
      // title and image are mutually exclusive, so setting title (must be non-empty string) hides the logo
      ...(image ? { image } : { title: " " }),
    });
  }

  navigateToMiniApp(miniAppId: string, miniAppPath?: string) {
    my?.navigateToMiniProgram({
      appId: miniAppId,
      path: miniAppPath ? encodeURI(miniAppPath) : "/",
    });
  }

  async signIn() {
    const authCode = await promiseWithTimeout<string>(
      (resolve, reject) => {
        my?.getAuthCode({
          scopes: ["auth_base", "USER_LOGIN_ID"], // silent log in without consents
          success: (res) => {
            resolve(res.authCode);
          },
          fail: (error) => {
            reject(
              new SignInError(
                `Failed to get auth code. Message: ${error.errorMessage}. Error code: ${error.error}`,
                error.error === 11
                  ? "cancelled"
                  : error.error === 10
                    ? "network_fail"
                    : "unknown",
              ),
            );
          },
        });
      },
      {
        timeoutMs: defaultSuperQiTimeoutMs,
        timeoutError: new SignInError("Getting auth code timed out", "timeout"),
      },
    );

    const { data } = await unauthenticatedApi.post<SignUpOutputDto>(
      "/auth/super-qi/sign-up",
      {
        authCode,
        miniProgramClient: "TRAVEL_ZONE",
      },
    );

    return data.data;
  }

  async updatePersonalData() {
    const authCode = await promiseWithTimeout<string>(
      (resolve, reject) => {
        my?.getAuthCode({
          scopes: ["auth_base", "USER_LOGIN_ID", "USER_NAME"],
          success: (res) => {
            resolve(res.authCode);
          },
          fail: (error) => {
            reject(
              new PersonalDataConsentError(
                `Failed to get auth code for personal data. Message: ${error.errorMessage}. Error code: ${error.error}`,
                error.error === 11
                  ? "cancelled"
                  : error.error === 10
                    ? "network_fail"
                    : "unknown",
              ),
            );
          },
        });
      },
      {
        timeoutMs: defaultSuperQiTimeoutMs,
        timeoutError: new PersonalDataConsentError(
          "Getting auth code for personal data timed out",
          "timeout",
        ),
      },
    );

    return void api.post<void, AxiosResponse<void>, UpdateDetailsBodyInputDto>(
      "/auth/super-qi/update-details",
      {
        authCode,
        miniProgramClient: "TRAVEL_ZONE",
      },
    );
  }

  getPaymentProvider(providerType: "bnpl"): BnplPaymentProvider;
  getPaymentProvider(providerType: "standard"): StandardPaymentProvider;
  getPaymentProvider(providerType: PaymentProviderType): PaymentProvider {
    switch (providerType) {
      case "bnpl":
        return bnplPaymentProvider;
      case "standard":
        return superQiPaymentProvider;
      default:
        throw new Error("Unsupported payment provider type");
    }
  }

  async prepareEligibilityCheck() {
    const { authCode } = await new Promise<{ authCode: string }>(
      (resolve, reject) => {
        // https://miniprogram.alipay.com/docs/miniprogram/mpdev/api_openapi_getauthcode
        my?.getAuthCode({
          scopes: ["auth_base", "USER_LOGIN_ID", "CARD_LIST"],
          success: (res) => {
            resolve({ authCode: res.authCode });
          },
          fail: (error) => {
            reject(
              new CreditCardsConsentError(
                `Failed to get auth code for credit card data. Message: ${error.errorMessage}. Error code: ${error.error}`,
                error.error === 11
                  ? "cancelled"
                  : error.error === 10
                    ? "network_fail"
                    : "unknown",
              ),
            );
          },
        });
      },
    );

    await api.post<void, AxiosResponse<void>, UpdateDetailsBodyInputDto>(
      "/auth/super-qi/update-details",
      {
        authCode,
        miniProgramClient: "TRAVEL_ZONE",
        shouldUpdateUserCards: true,
      },
    );

    if (!isProduction) {
      return { cardNumber: "6330153657143355" };
    }

    // No need to return anything, credit card info will be fetched from the DB
    return {};
  }

  async chooseImage() {
    return new Promise<File | null>((resolve) => {
      my.chooseImage({
        success: async (res) => {
          const fileUrl = res?.apFilePaths?.[0];
          if (fileUrl) {
            const response = await fetch(fileUrl);

            if (!response.ok) {
              throw new Error(
                `Failed to fetch image: ${response.status} ${response.statusText}`,
              );
            }

            const blob = await response.blob();
            const fileName = fileUrl.split("/").pop() || "image";

            resolve(
              new File([blob], fileName, {
                lastModified: new Date().getTime(),
                type: blob.type,
              }),
            );
          }
        },
        fail: (error) => {
          // User cancelled the image selection
          if (error.error === 11) {
            resolve(null);
            return;
          }

          throw new Error("Error choosing image:", { cause: error });
        },
      });
    });
  }
}

export const superQiApi: PlatformApi = new SuperQiApi();
